import update from 'immutability-helper';
import { createReducer } from '../modules/helpers';
import { ActionTypes } from '../constants/index';

const initState = {
  pages: [],
  domains: [],
  status: 'init', // init -> loading -> loaded -> refreshing -> loaded
};

const emptyPage = {
  urlsafe: 'empty__page__dummy__urlsafe',
  name: '',
  note: '',
  tutorial: false,
  status: 'loading',
  distribution: 0,
  subdomain: '',
  url: '',
  path: '',
  variants: [],
};

const tutorialPage = {
  urlsafe: 'empty__tutorial__page__dummy__urlsafe',
  name: '',
  note: '',
  tutorial: true,
  status: 'loading',
  distribution: 0,
  subdomain: '',
  url: '',
  path: '',
  variants: [],
};

const emptyVariant = {
  urlsafe: 'empty__variant__dummy__urlsafe',
  name: '',
  note: '',
  distribution: 100,
  status: 'loading',
  enabled: false,
};

export const pageReducer = createReducer(initState, {

  [ActionTypes.PAGE_GET_REQUEST](state) {
    return update(state, {
      pages: { $set: [] },
      status: { $set: 'loading' },
    });
  },
  [ActionTypes.PAGE_GET_SUCCESS](state, { payload }) {
    return update(state, {
      pages: { $set: payload.pages },
      status: { $set: 'loaded' },
    });
  },
  [ActionTypes.PAGE_GET_FAILURE](state) {
    return update(state, {
      pages: { $set: [] },
      status: { $set: 'loaded' },
    });
  },

  [ActionTypes.PAGE_REFRESH_REQUEST](state) {
    return update(state, {
      status: { $set: 'refreshing' },
    });
  },
  [ActionTypes.PAGE_REFRESH_SUCCESS](state, { payload }) {
    return update(state, {
      pages: { $set: payload.pages },
      status: { $set: 'loaded' },
    });
  },
  [ActionTypes.PAGE_REFRESH_FAILURE](state) {
    return update(state, {
      pages: { $set: [] },
      status: { $set: 'loaded' },
    });
  },

  [ActionTypes.PAGE_ADD_REQUEST](state) {
    if ((state.pages.length > 0)
      && ('status' in state.pages[0])
      && (state.pages[0].status === 'loading')) {
      return state;
    }
    return update(state, {
      pages: { $unshift: [emptyPage] },
    });
  },
  [ActionTypes.PAGE_ADD_SUCCESS](state, { payload }) {
    return update(state, {
      pages: {
        0: { $set: payload },
      },
    });
  },

  [ActionTypes.PAGE_TUTORIAL_ADD_REQUEST](state) {
    if ((state.pages.length > 0)
      && ('status' in state.pages[0])
      && (state.pages[0].status === 'loading')) {
      return state;
    }
    return update(state, {
      pages: { $unshift: [tutorialPage] },
    });
  },
  [ActionTypes.PAGE_TUTORIAL_ADD_SUCCESS](state, { payload }) {
    return update(state, {
      pages: {
        0: { $set: payload },
      },
    });
  },

  [ActionTypes.PAGE_TUTORIAL_ADD_FAILURE](state) {
    return update(state, {
      pages: { $splice: [[0, 1]] },
    });
  },

  [ActionTypes.PAGE_UPDATE_REQUEST](state, { payload }) {
    const { pageUrlsafe, changes } = payload;
    const foundIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);

    let updatedPage = state.pages[foundIndex];

    changes.forEach((change) => {
      const mapkey = change.key;
      const mapval = change.val;
      updatedPage = update(updatedPage, {
        [mapkey]: { $set: mapval },
      });
    });
    updatedPage = update(updatedPage, {
      'status': { $set: 'updating' },
    });

    return update(state, {
      pages: {
        [foundIndex]: { $set: updatedPage },
      },
    });
  },

  [ActionTypes.PAGE_UPDATE_SUCCESS](state, { payload }) {
    const foundIndex = state.pages.findIndex(x => x.urlsafe === payload.pageUrlsafe);
    // console.assert(foundIndex !== -1, 'Page to be updated is not accessible');
    const currentPage = state.pages[foundIndex];
    payload.page.variants = [];
    let updatedPage = update(payload.page, {
      variants: { $set: currentPage.variants },
    });

    updatedPage = update(updatedPage, {
      'status': { $set: 'loaded' },
    });

    return update(state, {
      pages: {
        [foundIndex]: { $set: updatedPage },
      },
    });
  },

  [ActionTypes.PAGE_UPDATE_FAILURE](state, { payload }) {
    const foundIndex = state.pages.findIndex(x => x.urlsafe === payload.pageUrlsafe);
    // console.assert(foundIndex !== -1, 'Page to be updated is not accessible');
    const currentPage = state.pages[foundIndex];
    payload.page.variants = [];
    let updatedPage = update(payload.page, {
      variants: { $set: currentPage.variants },
    });

    updatedPage = update(updatedPage, {
      'status': { $set: 'loaded' },
    });

    return update(state, {
      pages: {
        [foundIndex]: { $set: updatedPage },
      },
    });
  },

  [ActionTypes.PAGE_CHECK_SUBDOMAIN_REQUEST](state, { payload }) {
    const { pageUrlsafe } = payload;
    const foundIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundIndex !== -1, 'Page to be updated is not accessible');

    return update(state, {
      pages: {
        [foundIndex]: {
          subdomainCheckStatus: { $set: 'checking' }, // null -> checking -> checked | error
          subdomainCheckResponse: { $set: null },
        },
      },
    });
  },
  [ActionTypes.PAGE_CHECK_SUBDOMAIN_SUCCESS](state, { payload }) {
    const { pageUrlsafe } = payload;

    const foundIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundIndex !== -1, 'Page to be updated is not accessible');

    return update(state, {
      pages: {
        [foundIndex]: {
          subdomainCheckStatus: { $set: 'checked' },
          subdomainCheckResponse: { $set: payload },
        },
      },
    });
  },

  [ActionTypes.PAGE_CHECK_SUBDOMAIN_FAILURE](state, { payload }) {
    // we are currently not using check subdomain failure
    const { pageUrlsafe } = payload;
    const foundIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);

    return update(state, {
      pages: {
        [foundIndex]: {
          subdomainCheckStatus: { $set: 'error' },
          subdomainCheckResponse: { $set: null },
        },
      },
    });
  },

  [ActionTypes.PAGE_DELETE_REQUEST](state, { payload }) {
    const { pageUrlsafe } = payload;
    const foundIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundIndex !== -1, 'Page to be deleted is not accessible');

    return update(state, {
      pages: {
        [foundIndex]: {
          status: { $set: 'deleting' },
        },
      },
    });
  },

  [ActionTypes.PAGE_DELETE_SUCCESS](state, { payload }) {
    const { pageUrlsafe } = payload;
    const foundIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundIndex !== -1, 'Page to be deleted is not accessible');

    return update(state, {
      pages: { $splice: [[foundIndex, 1]] },
    });
  },
  [ActionTypes.PAGE_DELETE_FAILURE](state, { payload }) {
    const { pageUrlsafe } = payload;
    const foundIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundIndex !== -1, 'Page to be deleted is not accessible');

    return update(state, {
      pages: {
        [foundIndex]: {
          status: { $set: 'idle' },
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_ADD_REQUEST](state, { payload }) {
    const { pageUrlsafe } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page for which variant has to added is not accessible');

    // Avoid multiple add variant request before one is served
    const currentPage = state.pages[foundPageIndex];
    if ((currentPage.variants.length > 0) &&
      ('status' in currentPage.variants[0]) &&
      (currentPage.variants[0].status === 'loading')) {
      return state;
    }

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: { $unshift: [emptyVariant] },
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_ADD_SUCCESS](state, { payload }) {
    const { variant, pageUrlsafe } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page for which variant has to added is not accessible');

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: {
            0: { $set: variant },
          },
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_ADD_FAILURE](state) {
    // todo - add a check if variant[0] is loading
    return update(state, {
      pages: {
        variants: {
          $splice: [[0, 1]],
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_DELETE_REQUEST](state, { payload }) {
    const { pageUrlsafe, variantUrlsafe } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to deleted is not accessible');

    const currentPage = state.pages[foundPageIndex];
    const foundVariantIndex = currentPage.variants.findIndex(x => x.urlsafe === variantUrlsafe);
    // console.assert(foundVariantIndex !== -1, 'Variant to be deleted is not accessible');

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: {
            [foundVariantIndex]: {
              status: { $set: 'deleting' },
            },
          },
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_DELETE_SUCCESS](state, { payload }) {
    const { pageUrlsafe, variantUrlsafe } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to deleted is not accessible');

    const currentPage = state.pages[foundPageIndex];
    const foundVariantIndex = currentPage.variants.findIndex(x => x.urlsafe === variantUrlsafe);
    // console.assert(foundVariantIndex !== -1, 'Variant to be deleted is not accessible');

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: { $splice: [[foundVariantIndex, 1]] },
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_DELETE_FAILURE](state, { payload }) {
    const { pageUrlsafe, variantUrlsafe } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to deleted is not accessible');

    const currentPage = state.pages[foundPageIndex];
    const foundVariantIndex = currentPage.variants.findIndex(x => x.urlsafe === variantUrlsafe);
    // console.assert(foundVariantIndex !== -1, 'Variant to be deleted is not accessible');

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: {
            [foundVariantIndex]: {
              status: { $set: 'idle' },
            },
          },
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_CLONE_REQUEST](state, { payload }) {
    const { pageUrlsafe, variantUrlsafe } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to cloned is not accessible');

    const currentPage = state.pages[foundPageIndex];
    const foundVariantIndex = currentPage.variants.findIndex(x => x.urlsafe === variantUrlsafe);
    // console.assert(foundVariantIndex !== -1, 'Variant to be cloned is not accessible');

    const cloneVariant = currentPage.variants[foundVariantIndex];

    const newCloneVariant = update(cloneVariant, {
      urlsafe: { $set: 'xxxx-uniquedummy-clone-variant-key' },
    });

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: { $unshift: [newCloneVariant] },
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_CLONE_SUCCESS](state, { payload }) {
    const { pageUrlsafe, variant } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to cloned is not accessible');

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: {
            0: { $set: variant },
          },
        },
      },
    });
  },
  [ActionTypes.PAGE_VARIANT_CLONE_FAILURE](state, { payload }) {
    const { pageUrlsafe } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to cloned is not accessible');

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: { $splice: [[0, 1]] },
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_UPDATE_REQUEST](state, { payload }) {

    const { pageUrlsafe, variantUrlsafe, changes } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to updated is not accessible');

    const currentPage = state.pages[foundPageIndex];
    const foundVariantIndex = currentPage.variants.findIndex(x => x.urlsafe === variantUrlsafe);
    // console.assert(foundVariantIndex !== -1, 'Variant to be updated is not accessible');

    const currentVariant = currentPage.variants[foundVariantIndex];
    let newVariant = null;
    for (let i = 0; i < changes.length; i++) {
      const { key, val } = changes[i];
      newVariant = update(currentVariant, {
        [key]: { $set: val },
      });
    }

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: {
            [foundVariantIndex]: { $set: newVariant },
          },
        },
      },
    });
  },
  [ActionTypes.PAGE_VARIANT_UPDATE_SUCCESS](state, { payload }) {
    const { pageUrlsafe, variantUrlsafe, variant } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to updated is not accessible');

    const currentPage = state.pages[foundPageIndex];
    const foundVariantIndex = currentPage.variants.findIndex(x => x.urlsafe === variantUrlsafe);
    // console.assert(foundVariantIndex !== -1, 'Variant to be updated is not accessible');

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: {
            [foundVariantIndex]: { $set: variant },
          },
        },
      },
    });
  },
  [ActionTypes.PAGE_VARIANT_UPDATE_FAILURE](state) {
    return state;
  },

  [ActionTypes.PAGE_UDPATE_DISTRIBUTION_LOCAL](state, { payload }) {
    const { pageUrlsafe, variantUrlsafe, value } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to updated is not accessible');

    const currentPage = state.pages[foundPageIndex];
    const foundVariantIndex = currentPage.variants.findIndex(x => x.urlsafe === variantUrlsafe);
    //  console.assert(foundVariantIndex !== -1, 'Variant to be updated is not accessible');

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: {
            [foundVariantIndex]: {
              distribution: { $set: value },
            },
          },
        },
      },
    });
  },

  [ActionTypes.PAGE_VARIANT_TUTORIAL_UPDATE_LOCAL](state, { payload }) {
    const { pageUrlsafe, variantUrlsafe, value } = payload;
    const foundPageIndex = state.pages.findIndex(x => x.urlsafe === pageUrlsafe);
    // console.assert(foundPageIndex !== -1, 'Page whose variant has to updated is not accessible');

    const currentPage = state.pages[foundPageIndex];
    const foundVariantIndex = currentPage.variants.findIndex(x => x.urlsafe === variantUrlsafe);
    // console.assert(foundVariantIndex !== -1, 'Variant to be updated is not accessible');

    return update(state, {
      pages: {
        [foundPageIndex]: {
          variants: {
            [foundVariantIndex]: {
              tutorialPopulated: { $set: value },
            },
          },
        },
      },
    });
  },

});



// WEBPACK FOOTER //
// ./scripts/reducers/page.js